import { Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import "../../styles/components/forms.css";

export default function FeedbackManagement() {
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [reactionFilter, setReactionFilter] = useState("all");
  const [activityFilter, setActivityFilter] = useState("all");

  useEffect(() => {
    const fetchFeedback = async () => {
      try {
        setLoading(true);
        setError("");
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/activities/feedback/admin/all`,
          { withCredentials: true }
        );
        setFeedback(response.data?.feedback || []);
      } catch (err) {
        setError(err.response?.data?.message || "Kunne ikke hente tilbakemeldinger.");
      } finally {
        setLoading(false);
      }
    };

    fetchFeedback();
  }, []);

  const activityOptions = useMemo(() => {
    const unique = new Set(
      feedback
        .map((item) => item.activityName)
        .filter((name) => typeof name === "string" && name.trim() !== "")
    );
    return Array.from(unique).sort((a, b) => a.localeCompare(b, "no"));
  }, [feedback]);

  const filteredFeedback = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return feedback.filter((item) => {
      if (reactionFilter === "positive" && item.isLiked !== 1) return false;
      if (reactionFilter === "negative" && item.isLiked !== 0) return false;
      if (reactionFilter === "none" && (item.isLiked === 1 || item.isLiked === 0)) return false;
      if (activityFilter !== "all" && item.activityName !== activityFilter) return false;
      if (!normalizedQuery) return true;

      const haystack = [
        item.activityName,
        item.userName,
        item.userEmail,
        item.feedbackComment,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(normalizedQuery);
    });
  }, [feedback, query, reactionFilter, activityFilter]);

  return (
    <div className="page-container">
      <div className="page-header-stack">
        <Link to="/dashboard" className="back-button">
          <i className="fas fa-arrow-left"></i> Tilbake til hovedsiden
        </Link>
        <h1>Brukerfeedback</h1>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: "1rem",
          marginBottom: "1rem",
        }}
      >
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label htmlFor="feedback-search">Søk i feedback</label>
          <input
            id="feedback-search"
            type="text"
            className="form-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Aktivitet, navn, e-post eller kommentar"
          />
        </div>

        <div className="form-group" style={{ marginBottom: 0 }}>
          <label htmlFor="feedback-reaction-filter">Filtrer på vurdering</label>
          <select
            id="feedback-reaction-filter"
            className="form-select"
            value={reactionFilter}
            onChange={(e) => setReactionFilter(e.target.value)}
          >
            <option value="all">Alle</option>
            <option value="positive">Kun positive</option>
            <option value="negative">Kun negative</option>
            <option value="none">Ingen vurdering</option>
          </select>
        </div>

        <div className="form-group" style={{ marginBottom: 0 }}>
          <label htmlFor="feedback-activity-filter">Filtrer på aktivitet</label>
          <select
            id="feedback-activity-filter"
            className="form-select"
            value={activityFilter}
            onChange={(e) => setActivityFilter(e.target.value)}
          >
            <option value="all">Alle aktiviteter</option>
            {activityOptions.map((activityName) => (
              <option key={activityName} value={activityName}>
                {activityName}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading && <div className="list-item">Laster feedback...</div>}
      {!loading && error && <div className="error-message">{error}</div>}

      {!loading && !error && (
        <div className="activities-list">
          <h2>
            Tilbakemeldinger ({filteredFeedback.length}
            {query ? ` av ${feedback.length}` : ""})
          </h2>
          <div className="list-container">
            {filteredFeedback.length === 0 ? (
              <div className="list-item">Ingen tilbakemeldinger funnet</div>
            ) : (
              filteredFeedback.map((item, index) => (
                <div
                  key={`${item.activityId}-${item.userId}-${item.feedbackUpdatedAt || index}`}
                  className="list-item"
                  style={{ display: "block", position: "relative", paddingTop: "1.2rem" }}
                >
                  {(item.isLiked === 1 || item.isLiked === 0) && (
                    <span
                      title={item.isLiked === 1 ? "Tommel opp" : "Tommel ned"}
                      aria-label={item.isLiked === 1 ? "Tommel opp" : "Tommel ned"}
                      style={{
                        position: "absolute",
                        top: "0.75rem",
                        right: "0.9rem",
                        fontSize: "2rem",
                        lineHeight: 1,
                      }}
                    >
                      {item.isLiked === 1 ? "👍" : "👎"}
                    </span>
                  )}
                  <p>
                    <strong>Aktivitet:</strong> {item.activityName || "Ukjent aktivitet"}
                  </p>
                  <p>
                    <strong>Bruker:</strong> {item.userName || "Ukjent"} ({item.userEmail || "-"})
                  </p>
                  <p>
                    <strong>Vurdering:</strong>{" "}
                    {item.isLiked === 1 ? (
                      "Tommel opp"
                    ) : item.isLiked === 0 ? (
                      "Tommel ned"
                    ) : (
                      "Ingen"
                    )}
                  </p>
                  <p>
                    <strong>Kommentar:</strong> {item.feedbackComment || "-"}
                  </p>
                  <p>
                    <strong>Oppdatert:</strong>{" "}
                    {item.feedbackUpdatedAt
                      ? new Date(item.feedbackUpdatedAt).toLocaleString("no-NO")
                      : "-"}
                  </p>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
